import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import profile from "../../../../assets/images/icons/profile-blue.svg";
import warning from "../../../../assets/images/icons/warning.svg";
import { PatientDetails } from "../../../../interface/GeneralResponse";
import { RootState } from "../../../../redux/store";
import { useToast } from "../../../../Services/ToastContext";
import { useAppSelector, usePostData } from "../../../../hooks";
import { EndPoints } from "../../../../Constants/endpoints";
import {
  DateInput,
  GenderRadioSelect,
  GeneralInput,
  PhoneAutocompleteInput,
  ProfileAvatar,
} from "../../../../components";
import { truncateText } from "../../../../utils/stringsUtils";

interface PatientDetailsFormProps {
  onSubmit: (data: PatientDetails) => void;
 }

const AddNewPatient: React.FC<PatientDetailsFormProps> = ({
  onSubmit: onSubmitProp,
 }) => {
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const [selectedPatient, setSelectedPatient] = useState<
    PatientDetails | undefined
  >();
  const [createdPatientToSend, setCreatedPatientToSend] =
    useState<PatientDetails | null>(null);

  const [hidePatientInfoInputs, setHidePatientInfoInputs] =
    useState<boolean>(true);

  const { showToast } = useToast();
  const validate = useSelector((state: RootState) => state.phValidation);
  const registration = useAppSelector((state: RootState) => state.registration);
  const countryId = Number(registration.userDetails.CountryId);

  const patientDetailsSchema = useMemo(
    () =>
      z.object(
        !selectedPatient
          ? {
              // PhoneNumber: z.any().optional(),
              FullName: z
                .string()
                .min(4, { message: `${t("fullname_min_length_4")}` })
                .optional(),
              PhoneNumber: z
                .string()
                .min(9, t("phone_number_required"))
                .optional()
                .superRefine((value, ctx) => {
                  if (!validate) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: t("validation_configuration_error"),
                    });
                    return;
                  }

                  const PhoneNumber = value?.replace(/[^0-9]/g, "");
                  const countryStart = validate.Start.toString();
                  const countryLength = validate.Length;
                  const startWith0 = `0${countryStart}`;
                  const startWithCountryStart = `${countryStart}`;

                  if (PhoneNumber?.startsWith(startWith0)) {
                    if (PhoneNumber?.length !== countryLength + 1) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: t("phone_number_must_be_and_start_with", {
                          length: countryLength + 1,
                          start: startWith0,
                        }),
                      });
                    }
                  } else if (PhoneNumber?.startsWith(startWithCountryStart)) {
                    if (PhoneNumber?.length !== countryLength) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: t("phone_number_must_be_and_start_with", {
                          length: countryLength,
                          start: startWithCountryStart,
                        }),
                      });
                    }
                  } else {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: t("phone_number_must_start_with", {
                        start1: startWithCountryStart,
                        start2: startWith0,
                      }),
                    });
                  }
                }),
              Birthday: z
                .string()
                .min(1, { message: t("birth_date_required") })
                .optional(),
              Gender: z
                .string()
                .min(1, { message: t("please_select_your_gender") })
                .optional(),
            }
          : {
              patientSearch: z.any().optional(), 
              Birthday: selectedPatient?.Birthday
                ? z.string().optional()
                : z.string().min(1, {
                    message: t("birth_date_required"),
                  }),

              Gender: selectedPatient?.Gender
                ? z.string().optional()
                : z.string().min(1, {
                    message: t("please_select_your_gender"),
                  }),
            },
      ),
    [selectedPatient, validate],
  );

  type PatientDetailsFormData = z.infer<typeof patientDetailsSchema>;

  const methods = useForm<PatientDetailsFormData>({
    resolver: zodResolver(patientDetailsSchema),
    defaultValues: {},
  });

  const { control, handleSubmit, reset, watch } = methods;

  const { mutate: createPatient, isPending } = usePostData(
    EndPoints.CreatePatient,
  );

  const phoneValue = watch("PhoneNumber");
  const handleClearSelection = () => {
    reset({
      PhoneNumber: phoneValue,
      FullName: "",
      Birthday: "",
      Gender: "",
    });
    setSelectedPatient(undefined);
  };

  useEffect(() => {
    if (!isPending && createdPatientToSend) {
      onSubmitProp(createdPatientToSend);

      setCreatedPatientToSend(null);
    }
  }, [isPending, createdPatientToSend, onSubmitProp]);

  const onOptionSelectPatient = (option: PatientDetails) => {
    setSelectedPatient({ ...option });
    methods.reset(option);
  };

  const onSubmit: SubmitHandler<PatientDetailsFormData> = (data) => {
    if (isPending) return;

  if (selectedPatient?.Id) {
    const patientToSend: PatientDetails = {
      ...selectedPatient,

      Birthday: selectedPatient.Birthday || (data as any).Birthday,

      Gender: selectedPatient.Gender || (data as any).Gender,
    };

    onSubmitProp(patientToSend);
    return;
  }

    let PhoneNumber = (data as any).PhoneNumber as string;
    if (PhoneNumber?.startsWith("0")) PhoneNumber = PhoneNumber.substring(1);

    const requestData = {
      FullName: (data as any).FullName,
      PhoneNumber: PhoneNumber,
      Birthday: (data as any).Birthday,
      Gender: (data as any).Gender,
      CountryId: countryId,
    };

    createPatient(requestData, {
      onSuccess: (response: any) => {
        showToast(t("patient_created_successfully"), "success");
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetMyPatient],
          exact: false,
        });

        const patient: PatientDetails = response?.Data;
        if (!patient?.Id) {
          showToast(t("failed_to_create_patient"), "error");
          return;
        }

        setCreatedPatientToSend(patient);
      },
      onError: (error: any) => {
        showToast(
          error?.message ? error.message : t("failed_to_create_patient"),
          "error",
        );
      },
    });
  };

  const isMaskedSelected = !!selectedPatient?.FullName?.includes("*");
const isExistingPatient = Boolean(selectedPatient?.Id);
const patientHasBirthday = Boolean(selectedPatient?.Birthday);
const patientHasGender = Boolean(selectedPatient?.Gender);

const blockInputsBeforeSelection = hidePatientInfoInputs && !isExistingPatient;
  return (
    <FormProvider {...methods}>
      <form
        id="patient-details-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-4"
      >
        {isMaskedSelected && (
          <div className="flex items-center gap-4 rounded-lg border border-orange bg-warning-50 p-2 text-sm font-bold text-orange">
            <img src={warning} alt="" className="h-6 w-6" />
            <p>{t("this_user_is_already_have_an_account_on_dome_care")}</p>
          </div>
        )}

        <PhoneAutocompleteInput<{ Data: PatientDetails[] }, PatientDetails>
          name="PhoneNumber"
          controllerName="PhoneNumber"
          control={control}
          placeholder={t("search_about_user_by_number")}
          isOptional={false}
          getEndpoint={EndPoints.SearchMyPatients}
          onInputChange={() => {
            handleClearSelection();
          }}
          onSearchResults={(options) => {
            if (!options || options?.length == 0) {
              handleClearSelection();
            }
            setHidePatientInfoInputs(options?.length > 0);
          }}
          inputProps={{ maxLength: 60, disabled: isPending }}
          getOptionLabel={(option: PatientDetails) => {
            return {
              value: `${option.PhoneNumber}`,
              label: (
                <div className="flex items-center gap-4 hover:text-primary">
                  {option?.FullName?.includes("*") ? (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                      <img src={profile} alt="profile dome care" />
                    </div>
                  ) : (
                    <ProfileAvatar
                      Id={option?.Id}
                      FullName={option?.FullName}
                      src={option?.Image}
                      size="small"
                      className="h-10 w-10 rounded-full object-cover object-top"
                    />
                  )}
                  <div className="text-sm font-bold">
                    {truncateText(option?.FullName, 30)}
                  </div>
                </div>
              ),
            };
          }}
          getOptionValue={(option: PatientDetails) => option.Id.toString()}
          onOptionSelect={(option: PatientDetails) =>
            onOptionSelectPatient(option)
          }
        />

        <div className="relative flex flex-col gap-4">
          {blockInputsBeforeSelection && (
            <div className="absolute inset-0 z-10 bg-white/20" />
          )}

          <GeneralInput
            name="FullName"
            label={t("full_name")}
            placeholder={t("full_name")}
            required
            maxLength={60}
            disabled={isPending}
            readOnly={blockInputsBeforeSelection || isExistingPatient}
          />

          {isExistingPatient && patientHasBirthday ? (
            <GeneralInput
              name="Birthday"
              label={t("birth_date")}
              placeholder="YYYY-MM-DD"
              disabled={isPending}
              readOnly
            />
          ) : (
            <DateInput
              maxDate={moment().toDate()}
              label={t("birth_date")}
              name="Birthday"
              disabled={isPending || blockInputsBeforeSelection}
              
            />
          )} 
          <GenderRadioSelect
            name="Gender"
            readOnly={
              isPending ||
              blockInputsBeforeSelection ||
              (isExistingPatient && patientHasGender)
            }
            disabled={
              isPending ||
              blockInputsBeforeSelection ||
              (isExistingPatient && patientHasGender)
            }
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default AddNewPatient;
